import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import i18n from '@app/i18n'

import { useModalStore } from '@features/modal'
import InfoModal from '@features/modal/ui/error'
import styles from '@features/modal/ui/styles.module.scss'

import { client } from '@shared/api/client'
import { SHOP_ID } from '@shared/contants'

import { checkProductsInStock, loadCarts, syncCart, } from '../api'

import type { CartData, CartInfoInput, CartProduct, SaleApplyDecisionInput, SaleSpendBonusesInput, StockProductInput } from '../types'
import type { GQL_ID } from '@@types'

const modalStore = useModalStore.getState()

const t = i18n.t

const openErrorInfoModal = (errorTransKey?: string) => {
  modalStore.openModal('info', <InfoModal />, {
    title: t('common:modal.error.title'),
    description:
      t(`errors:${errorTransKey}`) ?? t('common:modal.error.description'),
    classes: {
      content: styles['error-info-modal-content'],
    },
  })
}

export interface CartInfoQueryVariables {
  cartInfo: CartInfoInput
  style?: string
  imageStyle2?: string
  imagesStyle2?: string
  previewStyle2?: string
}

interface CartState {
  carts: Record<string, CartData>
  currentShopId: GQL_ID
  isSyncing: boolean
  pendingRemovals: Map<string, { productId: GQL_ID; previousAmount: number }>
  removingProducts: Set<string>

  setIsSyncing: (value: boolean) => void
  setCarts: (carts: Record<string, CartData>) => void
  setPendingRemoval: (productId: GQL_ID, previousAmount: number) => void
  removePendingRemoval: (productId: GQL_ID) => void
  hasPendingRemoval: (productId: GQL_ID) => boolean
  getPendingRemoval: (productId: GQL_ID) => { productId: GQL_ID; previousAmount: number } | undefined
  setRemovingProduct: (productId: GQL_ID) => void
  clearRemovingProduct: (productId: GQL_ID) => void

  setShop: (shopId: GQL_ID) => void
  addOrUpdateProduct: (product: CartProduct) => void
  removeProduct: (productId: GQL_ID) => void
  clearCart: () => void
  setPromocode: (promocode: string) => void
  setAppliedBonuses: (bonuses: SaleSpendBonusesInput[]) => void
  clearAppliedBonuses: () => void
  getTotalQuantity: () => number
  getCart: () =>
    | { shop_id: GQL_ID; items: CartProduct[]; promocode?: string; appliedBonuses?: SaleSpendBonusesInput[]; appliedSales?: SaleApplyDecisionInput[] }
    | undefined
  isProductInCart: (productId: GQL_ID) => boolean
  getProduct: (productId: GQL_ID) => CartProduct | undefined
  updateProductComment: (productId: GQL_ID, comment: string) => void
  hasComment: (productId: GQL_ID) => boolean
  removeProductComment: (productId: GQL_ID) => void
  loadCarts: () => Promise<void>
  syncCart: (currentCart?: CartData, withSoldOut?: boolean) => Promise<void>
  checkProductsInStock: () => Promise<boolean | undefined>
  removeZeroAmountItemsInCurrentCart: (isAuthorized: boolean) => Promise<void>
  setCurrentCart: (cart: CartData) => void
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      carts: {},
      currentShopId: SHOP_ID,
      isSyncing: false,
      pendingRemovals: new Map(),
      removingProducts: new Set(),

      setIsSyncing: (value) => set({ isSyncing: value }),

      setCarts: (carts) => set({ carts }),

      setPendingRemoval: (productId, previousAmount) => {
        set((state) => {
          const next = new Map(state.pendingRemovals)
          next.set(String(productId), { productId, previousAmount })
          return { pendingRemovals: next }
        })
      },

      removePendingRemoval: (productId) => {
        set((state) => {
          const next = new Map(state.pendingRemovals)
          next.delete(String(productId))
          return { pendingRemovals: next }
        })
      },

      hasPendingRemoval: (productId) => {
        return get().pendingRemovals.has(String(productId))
      },

      getPendingRemoval: (productId) => {
        return get().pendingRemovals.get(String(productId))
      },

      setRemovingProduct: (productId) => {
        set((state) => {
          const next = new Set(state.removingProducts)
          next.add(String(productId))
          return { removingProducts: next }
        })
      },

      clearRemovingProduct: (productId) => {
        set((state) => {
          const next = new Set(state.removingProducts)
          next.delete(String(productId))
          return { removingProducts: next }
        })
      },

      setShop: (shopId) => {
        set((state) => ({
          currentShopId: shopId,
          carts: {
            ...state.carts,
            [shopId]: state.carts[shopId] ?? {
              items: [],
              promocode: undefined,
              appliedBonuses: [],
              appliedSales: [],
            },
          },
        }))
      },

      addOrUpdateProduct: (product) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId] ?? {
            items: [],
            promocode: undefined,
            appliedBonuses: [],
            appliedSales: [],
          }
          const idx = cart.items.findIndex(
            (p) => String(p.product_id) === String(product.product_id),
          )
          const items = [ ...cart.items ]

          if (idx !== -1) items[idx] = { ...items[idx], ...product }
          else items.push(product)

          return {
            carts: { ...state.carts, [shopId]: { ...cart, items } },
          }
        })
      },

      removeProduct: (productId) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId]
          if (!cart) return {}
          return {
            carts: {
              ...state.carts,
              [shopId]: {
                ...cart,
                items: cart.items.filter((p) => String(p.product_id) !== String(productId)),
              },
            },
          }
        })
      },

      clearCart: () => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => ({
          carts: {
            ...state.carts,
            [shopId]: { items: [], promocode: undefined, appliedBonuses: [] },
          },
          // Очищаем также pendingRemovals и removingProducts
          pendingRemovals: new Map(),
          removingProducts: new Set(),
        }))
      },

      setPromocode: (promocode) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId] ?? {
            items: [],
            promocode: undefined,
            appliedBonuses: [],
            appliedSales: [],
          }
          return {
            carts: { ...state.carts, [shopId]: { ...cart, promocode } },
          }
        })
      },

      setAppliedBonuses: (bonuses) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId] ?? {
            items: [],
            promocode: undefined,
            appliedBonuses: [],
            appliedSales: [],
          }
          return {
            carts: { ...state.carts, [shopId]: { ...cart, appliedBonuses: bonuses } },
          }
        })
      },

      clearAppliedBonuses: () => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId]
          if (!cart) return {}
          return {
            carts: { ...state.carts, [shopId]: { ...cart, appliedBonuses: [] } },
          }
        })
      },

      getCart: () => {
        const shopId = get().currentShopId
        if (!shopId) return undefined
        const cart = get().carts[shopId]
        return cart
          ? { shop_id: shopId, items: cart.items, promocode: cart.promocode, appliedBonuses: cart.appliedBonuses ?? [] }
          : undefined
      },

      isProductInCart: (productId) => {
        const shopId = get().currentShopId
        return (
          !!shopId &&
          !!get().carts[shopId]?.items.some((p) => String(p.product_id) === String(productId))
        )
      },

      getProduct: (productId) => {
        const shopId = get().currentShopId
        return shopId
          ? get().carts[shopId]?.items.find((p) => String(p.product_id) === String(productId))
          : undefined
      },

      getTotalQuantity: () => {
        const shopId = get().currentShopId
        if (!shopId) return 0
        const cart = get().carts[shopId]
        return cart?.items?.length ?? 0
      },

      updateProductComment: (productId, comment) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId]
          if (!cart) return {}
          return {
            carts: {
              ...state.carts,
              [shopId]: {
                ...cart,
                items: cart.items.map((item) =>
                  String(item.product_id) === String(productId) ? { ...item, comment } : item,
                ),
              },
            },
          }
        })
      },

      hasComment: (productId) => {
        const shopId = get().currentShopId
        const product = shopId
          ? get().carts[shopId]?.items.find((p) => String(p.product_id) === String(productId))
          : undefined
        return !!product?.comment?.trim()
      },

      removeProductComment: (productId) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId]
          if (!cart) return {}
          return {
            carts: {
              ...state.carts,
              [shopId]: {
                ...cart,
                items: cart.items.map((item) =>
                  String(item.product_id) === String(productId)
                    ? { ...item, comment: undefined }
                    : item,
                ),
              },
            },
          }
        })
      },

      loadCarts: async () => {
        try {
          set({
            isSyncing: true,
          })

          const serverCarts = await loadCarts(client)

          // Преобразуем серверные корзины в формат локального стейта
          const carts: Record<string, CartData> = {}
          serverCarts.data.loadCarts.forEach((serverCart) => {
            carts[serverCart.shop_id] = {
              items: serverCart.items,
              promocode: serverCart.promocode,
              appliedBonuses: [],
              appliedSales: [],
            }
          })

          // Заменяем локальный стейт серверным
          set(() => ({
            carts,
          }))

          await get().checkProductsInStock()
        }
        catch (error) {
          console.error('Ошибка загрузки корзин', error)
          openErrorInfoModal('load_carts_error')
        }
        finally {
          set({
            isSyncing: false,
          })
        }
      },

      syncCart: async (cart, withSoldOut = true) => {
        const shopId = get().currentShopId
        const currentCart = cart ?? get().getCart()

        if (!shopId || !currentCart) return

        try {
          set({ isSyncing: true })

          // Убираем только товары с нулевым количеством, раскупленные передаем
          const itemsToSync = currentCart.items
            .filter((item) => item.amount > 0 && !item.isSoldOut)
            .map(({ product_id, amount, comment, offer_id }) => ({
              product_id,
              amount,
              ...(comment && { comment }),
              ...(offer_id && { offer_id }),
            }))

          const response = await syncCart(client, {
            cart: {
              shop_id: shopId,
              items: itemsToSync,
              sale: {
                spendBonuses: currentCart.appliedBonuses ?? [],
                coupons: currentCart.promocode ? [ { barcode: currentCart.promocode } ] : [],
                applySales: currentCart.appliedSales ?? [],
                promocodes: []
              },
            },
          })

          const updatedItems = response.data?.syncCart

          if (updatedItems?.length) {
            set((state) => {
              // Создаём Map для быстрого поиска существующих товаров
              const existingItemsMap = new Map(
                currentCart.items.map(item => [ String(item.product_id), item ])
              )

              // Создаём Map обновлённых товаров
              const updatedItemsMap = new Map(
                updatedItems.map((item: CartProduct) => [ String(item.product_id), item ])
              )

              // Собираем все товары без дублей
              const allItems: CartProduct[] = []

              // Добавляем обновленные товары с сервера
              updatedItems.forEach(({ product_id, amount }: CartProduct) => {
                const existingItem = existingItemsMap.get(String(product_id))
                allItems.push({
                  ...existingItem,
                  product_id,
                  amount,
                  // Сохраняем isSoldOut из исходного товара, если его нет - ставим false
                  isSoldOut: existingItem?.isSoldOut ?? false,
                })
              })

              // Добавляем раскупленные товары, которых НЕТ в обновлённых (только если withSoldOut = true)
              if (withSoldOut) {
                currentCart.items.forEach(item => {
                  if (item.isSoldOut && !updatedItemsMap.has(String(item.product_id))) {
                    allItems.push(item)
                  }
                })
              }

              return {
                carts: {
                  ...state.carts,
                  [shopId]: {
                    ...state.carts[shopId],
                    items: allItems,
                  },
                },
              }
            })
          }
        }
        catch (error) {
          console.error('Ошибка синхронизации корзины', error)
          openErrorInfoModal('sync_cart')
        }
        finally {
          set({ isSyncing: false })
        }
      },

      checkProductsInStock: async () => {
        let hasChanges = false

        try {
          set({
            isSyncing: true,
          })

          const shopId = get().currentShopId
          const cart = get().getCart()

          if (!shopId || !cart?.items) return

          const cartData: StockProductInput = {
            shop_id: shopId,
            items: cart?.items.map((i) => {
              return {
                product_id: i.product_id,
                amount: i.amount,
              }
            }),
          }

          const data = await checkProductsInStock(client, { productData: cartData })

          const checkProductsData = data.data
            ?.checkProductsInStock

          if (checkProductsData) {
            const cart = get().getCart()

            for (const checkProduct of checkProductsData) {
              const findProductInCart = cart?.items.find(
                (i) => String(i.product_id) === String(checkProduct.product_id),
              )

              if (!findProductInCart) continue

              const maxAmount = checkProduct.max_amount ?? null
              const errorCode = checkProduct.error_code

              // Если товар раскуплен: STOCK_ERROR_OUT_OF_RANGE (2) И max_amount === 0
              if (errorCode === 2 && maxAmount === 0) {
                get().addOrUpdateProduct({
                  product_id: checkProduct.product_id,
                  amount: findProductInCart.amount, // Сохраняем оригинальное количество
                  isSoldOut: true,
                })
                hasChanges = true
              }
              // Если товар удален: STOCK_ERROR_PRODUCT_NOT_FOUND (1)
              else if (errorCode === 1) {
                get().removeProduct(checkProduct.product_id)
                hasChanges = true
              }
              // Если количество превышает доступное: STOCK_ERROR_OUT_OF_RANGE (2) И max_amount > 0
              else if (errorCode === 2 && maxAmount !== null && maxAmount > 0 && findProductInCart.amount > maxAmount) {
                get().addOrUpdateProduct({
                  product_id: checkProduct.product_id,
                  amount: maxAmount,
                  isSoldOut: false,
                })
                hasChanges = true
              }
              // Если товар снова в наличии: STOCK_ERROR_NO_ERRORS (0) или нет ошибки
              else if (findProductInCart.isSoldOut && (!errorCode || errorCode === 0)) {
                get().addOrUpdateProduct({
                  product_id: checkProduct.product_id,
                  amount: findProductInCart.amount > 0 ? findProductInCart.amount : 1,
                  isSoldOut: false,
                })
                hasChanges = true
              }
            }
          }
        }
        catch (error) {
          console.error('Ошибка проверки наличия товара', error)
          openErrorInfoModal('check_products_in_stock')
        }
        finally {
          set({
            isSyncing: false,
          })
        }

        return hasChanges
      },
      setCurrentCart: (cart) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => ({
          carts: {
            ...state.carts,
            [shopId]: { ...cart, appliedBonuses: cart.appliedBonuses ?? [] },
          },
        }))
      },

      removeZeroAmountItemsInCurrentCart: async (isAuthorized: boolean) => {
        const shopId = get().currentShopId
        if (!shopId) return

        const cart = get().carts[shopId]
        if (!cart) return

        // Сохраняем товары с amount > 0 ИЛИ isSoldOut товары (они имеют amount = 0, но должны остаться)
        const filteredItems = cart.items.filter((item) => item.amount > 0 || item.isSoldOut)
        if (filteredItems.length === cart.items.length) return

        set((state) => ({
          carts: {
            ...state.carts,
            [shopId]: {
              ...cart,
              items: filteredItems,
            },
          },
        }))

        if (isAuthorized) {
          await get().syncCart()
        }
      },
    }),
    {
      name: 'cart-storage',
      partialize: (state) => ({
        carts: state.carts,
        currentShopId: state.currentShopId,
      }),
    },
  ),
)
